/**
 * Data API Service (via Firebase Cloud Function)
 *
 * This service calls the Firebase Cloud Function that securely accesses
 * the data API to fetch EVM address data.
 *
 * Security: API key is kept secure on the backend, not exposed to frontend
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import app from './firebase';

// Initialize Firebase Functions
const functions = getFunctions(app);

// Debug flag - can be toggled from browser console: window.dataServiceDebug = true
let isDebug = false;
Object.defineProperty(window, 'dataServiceDebug', {
  get: () => isDebug,
  set: (value) => { isDebug = !!value; }
});

/**
 * Get EVM address data from Moralis via cloud function
 *
 * @param {string} address - EVM address (0x...), assumed normalized
 * @param {string} chain - Chain name (ethereum, polygon, bsc, arbitrum, optimism, base, avalanche)
 * @returns {Promise<Object>} Address data including balance, transaction history, first/last tx times
 */
export const getEVMAddressData = async (address, chain) => {
  // Choose function based on environment
  // Use dev function when REACT_APP_USE_DEV_FUNCTIONS=true
  const useDevFunction = process.env.REACT_APP_USE_DEV_FUNCTIONS === 'true';
  const functionName = useDevFunction ? 'getEVMAddressDataDev' : 'getEVMAddressData';

  try {
    // Call the cloud function
    const getAddressData = httpsCallable(functions, functionName);

    const result = await getAddressData({address, chain});

    if (!result.data || !result.data.success) {
      throw new Error('Failed to fetch address data');
    }
    // console.log(`Result of ${functionName}:\n${JSON.stringify(result.data, null, 2)}`);
    return result.data;
  } catch (error) {
    console.error(`Get ${functionName} error:`, error);

    // Extract user-friendly error message
    let errorMessage = 'Failed to fetch address data';

    if (error.code === 'functions/invalid-argument') {
      errorMessage = error.message || 'Invalid address or chain';
    } else if (error.code === 'functions/resource-exhausted') {
      errorMessage = error.message || 'Too many requests. Please try again later.';
    } else if (error.code === 'functions/internal') {
      errorMessage = 'Service temporarily unavailable. Please try again.';
    } else if (error.code === 'functions/not-found') {
      errorMessage = 'Cloud function not found. Please ensure the function is deployed.';
    }

    throw new Error(errorMessage);
  }
};

/**
 * Get coin security level from cloud function
 *
 * @param {string} coinId - CoinGecko coin ID (e.g., "bitcoin", "ethereum")
 * @returns {Promise<Object>} Coin security data including security level and platform details
 */
export const getCoinSecurityLevel = async (coinId) => {
  const useDevFunction = process.env.REACT_APP_USE_DEV_FUNCTIONS === 'true';
  const functionName = useDevFunction ? 'getCoinSecurityLevelDev' : 'getCoinSecurityLevel';
  try {
    // Call the cloud function
    const getCoinSecurity = httpsCallable(functions, functionName);

    const result = await getCoinSecurity({ coinId });

    if (!result.data || !result.data.success) {
      throw new Error('Failed to fetch coin security level');
    }

    // console.log(`Result of ${functionName}:\n${JSON.stringify(result.data, null, 2)}`);
    return result.data;
  } catch (error) {
    console.error(`Get ${functionName} error:`, error);
    throw error;
  }
};

/**
 * Get token holders for a contract address from cloud function
 *
 * @param {string} address - Contract address (0x...)
 * @param {string} chain - Chain name (ethereum, polygon, bsc, etc.)
 * @returns {Promise<Object>} Token holders data including totalHolders and holdersByAcquisition
 */
export const getTokenHolders = async (address, chain) => {
  try {
    // Call the cloud function
    const getHolders = httpsCallable(functions, 'getTokenHolders');

    const result = await getHolders({
      address: address.toLowerCase(),
      chain
    });

    if (!result.data || !result.data.success) {
      throw new Error('Failed to fetch token holders');
    }

    // console.log(`Result of getTokenHolders:\n${JSON.stringify(result.data, null, 2)}`);
    return result.data;
  } catch (error) {
    console.error('Get token holders error:', error);
    throw error;
  }
};

/**
 * Get aggregated token holders for a coin across all platforms
 *
 * @param {string} coinId - CoinGecko coin ID (e.g., "uniswap", "ethereum")
 * @param {boolean} forceRefresh - Force refresh from API, bypass cache (default: false)
 * @returns {Promise<Object>} Coin holders data including totalHolders across all platforms
 */
export const getCoinHolders = async (coinId, forceRefresh = false) => {
  try {
    // Call the cloud function
    const getHolders = httpsCallable(functions, 'getCoinHolders');

    const result = await getHolders({ coinId, forceRefresh });

    if (!result.data || !result.data.success) {
      throw new Error('Failed to fetch coin holders');
    }

    // console.log(`Result of getCoinHolders:\n${JSON.stringify(result.data, null, 2)}`);
    return result.data;
  } catch (error) {
    console.error('Get coin holders error:', error);
    throw error;
  }
};

// /**
//  * Get address net worth from cloud function (lazy loading)
//  *
//  * @param {string} address - EVM address (0x...)
//  * @param {string} chain - Chain name (ethereum, polygon, bsc, etc.)
//  * @returns {Promise<Object>} Net worth data including netWorthUsd
//  */
// export const getAddressNetWorth = async (address, chain) => {
//   try {
//     console.log(`📡 Calling getAddressNetWorth for ${chain}_${address}`);

//     const getNetWorth = httpsCallable(functions, 'getAddressNetWorth');

//     const result = await getNetWorth({
//       address: address.toLowerCase(),
//       chain
//     });

//     if (!result.data || !result.data.success) {
//       throw new Error('Failed to fetch address net worth');
//     }

//     if (isDebug) console.log(`Result of getAddressNetWorth:\n${JSON.stringify(result.data, null, 2)}`);
//     return result.data;
//   } catch (error) {
//     console.error('Get address net worth error:', error);
//     throw error;
//   }
// };

/**
 * Get address transaction timestamps from cloud function (lazy loading)
 *
 * @param {string} address - EVM address (0x...)
 * @param {string} chain - Chain name (ethereum, polygon, bsc, etc.)
 * @returns {Promise<Object>} Timestamps data including firstTransaction and lastTransaction
 */
export const getAddressTimestamps = async (address, chain) => {
  const useDevFunction = process.env.REACT_APP_USE_DEV_FUNCTIONS === 'true';
  const functionName = useDevFunction ? 'getAddressTimestampsDev' : 'getAddressTimestamps';
  try {
    const getTimestamps = httpsCallable(functions, functionName);

    const result = await getTimestamps({
      address: address.toLowerCase(),
      chain
    });

    if (!result.data || !result.data.success) {
      throw new Error('Failed to fetch address timestamps');
    }

    // console.log(`Result of ${functionName}:\n${JSON.stringify(result.data, null, 2)}`);
    return result.data;
  } catch (error) {
    console.error(`Get ${functionName} error:`, error);
    throw error;
  }
};

/**
 * Get address security level from cloud function (lazy loading)
 * Supports both single address and batch lookups
 *
 * @param {Array<{address: string, chain: string}>} addresses - Array of address/chain pairs
 * @param {Object} options - Optional parameters
 * @param {boolean} options.forceRefresh - Force refresh from API, bypass cache (default: false)
 * @returns {Promise<Array<Object>>} Array of security level results
 */
export const getAddressSecurityLevels = async (addresses, options = {}) => {
  const { forceRefresh = false } = options;
  const useDevFunction = process.env.REACT_APP_USE_DEV_FUNCTIONS === 'true';
  const functionName = useDevFunction ? 'getAddressSecurityLevelDev' : 'getAddressSecurityLevel';

  if (!addresses || addresses.length === 0) {
    return [];
  }

  // console.log(`📡 Calling ${functionName} for ${addresses.length} addresses, FR=${forceRefresh}`);

  try {
    const getSecurityLevel = httpsCallable(functions, functionName);

    const result = await getSecurityLevel({ addresses, forceRefresh });

    if (!result.data || !result.data.success) {
      throw new Error('Failed to fetch address security levels');
    }

    // console.log(`Result of ${functionName}:\n${JSON.stringify(result.data, null, 2)}`);
    return result.data.results || [];
  } catch (error) {
    console.error(`Get ${functionName} error:`, error);
    throw error;
  }
};

/**
 * Get address security level from cloud function (lazy loading)
 * Convenience wrapper for single address lookup
 *
 * @param {string} address - EVM address (0x...), assumed normalized
 * @param {string} chain - Chain name (ethereum, polygon, bsc, etc.)
 * @param {Object} options - Optional parameters
 * @param {boolean} options.forceRefresh - Force refresh from API, bypass cache (default: false)
 * @returns {Promise<Object>} Security level data { success, address, chain, securityLevel }
 */
export const getAddressSecurityLevel = async (address, chain, options = {}) => {
  const results = await getAddressSecurityLevels([{ address, chain }], options);
  if (results.length === 0) {
    throw new Error('Failed to fetch address security level');
  }
  return results[0];
};

/**
 * Get address contract status from cloud function (lazy loading)
 *
 * @param {string} address - EVM address (0x...)
 * @param {string} chain - Chain name (ethereum, polygon, bsc, etc.)
 * @returns {Promise<Object>} Contract status data including isContract
 */
export const getAddressIsContract = async (address, chain) => {
  try {
    const checkContract = httpsCallable(functions, 'getAddressIsContract');

    const result = await checkContract({
      address: address.toLowerCase(),
      chain
    });

    if (!result.data || !result.data.success) {
      throw new Error('Failed to fetch contract status');
    }

    // console.log(`Result of getAddressIsContract:\n${JSON.stringify(result.data, null, 2)}`);
    return result.data;
  } catch (error) {
    console.error('Get address contract status error:', error);
    throw error;
  }
};